import { HttpErrorResponse } from '@angular/common/http';
import * as Sentry from '@sentry/angular';
import {environment} from '@env/environment';

const APPLICATION = environment.openReplayApplication;
const recent = new Map<string, number>();

/** Allow-list the event; never send forms, HTTP bodies/headers, user details or console breadcrumbs. */
export function sanitizeErrorEvent(event: Sentry.ErrorEvent, hint?: Sentry.EventHint): Sentry.ErrorEvent | null {
    if (hint?.originalException instanceof HttpErrorResponse || !event.exception?.values?.length) { return null; }
    return {
        type: undefined, event_id: event.event_id, timestamp: event.timestamp, platform: event.platform, level: event.level,
        release: event.release, environment: event.environment, sdk: event.sdk,
        fingerprint: event.fingerprint,
        tags: Object.entries(event.tags || {}).filter(([key]) =>
            ['application', 'operation', 'http.status_code', 'http.method'].includes(key))
            .reduce((tags, [key, value]) => ({...tags, [key]: value}), {}),
        exception: {values: event.exception.values.map(exception => ({
            type: exception.type || 'Error', value: exception.type || 'Error',
            mechanism: exception.mechanism ? {type: exception.mechanism.type, handled: exception.mechanism.handled} : undefined,
            stacktrace: exception.stacktrace ? {frames: exception.stacktrace.frames?.map(frame => ({
                filename: frame.filename?.split(/[?#]/)[0], function: frame.function,
                module: frame.module, lineno: frame.lineno, colno: frame.colno, in_app: frame.in_app
            }))} : undefined
        }))}
    };
}

export function initializeErrorReporting(): void {
    Sentry.init({
        dsn: environment.glitchTipDsn,
        environment: environment.glitchTipEnvironment,
        release: `${APPLICATION}@16.1.0`,
        sendDefaultPii: false, tracesSampleRate: 0,
        defaultIntegrations: false,
        integrations: [Sentry.globalHandlersIntegration(), Sentry.linkedErrorsIntegration(), Sentry.dedupeIntegration()],
        beforeSend: sanitizeErrorEvent,
        beforeBreadcrumb: () => null,
        initialScope: {tags: {application: APPLICATION}}
    });
}

export function reportHandledError(error: unknown, operation: string, status?: number, method?: string): void {
    try {
        const failure = error instanceof Error ? error : new Error('HandledFailure');
        // A blocked browser store or a failing endpoint must not flood the server on each retry.
        const key = `${operation}:${failure.name}:${status || ''}:${method || ''}`;
        const now = Date.now();
        if (recent.has(key) && now - recent.get(key) < 60000) { return; }
        if (recent.size >= 100) { recent.clear(); }
        recent.set(key, now);
        Sentry.captureException(failure, {
            tags: {application: APPLICATION, operation, 'http.status_code': status, 'http.method': method},
            fingerprint: [APPLICATION, operation, failure.name, String(status || ''), method || '']
        });
    } catch {
        // Reporting is best effort; a telemetry failure must never block a sale.
    }
}

export function reportHttpError(error: HttpErrorResponse, method: string): void {
    if (error.status >= 500) {
        const failure = new Error(`HTTP_${error.status}`);
        failure.name = 'HttpServerError';
        reportHandledError(failure, 'http.server', error.status, method);
    }
}
